import { type ClerkAPIError } from '@clerk/types';
import { type ValidationError } from '@tanstack/react-form';

import { tNamespace } from '../services/translation';
import { data as errorsTranslations } from '../services/translations/data-errors';

export type ErrorAny = ClerkAPIError | ValidationError;

const t = tNamespace('errors');

export function mapErrorToTranslation(rawErrs?: ErrorAny | ErrorAny[]): string | undefined {
  const errors = Array.isArray(rawErrs) ? rawErrs : [rawErrs];
  if (errors == null || errors.length === 0) {
    return undefined;
  }

  const firstError = errors[0];
  if (firstError == null) {
    return undefined;
  }

  let errStr = typeof firstError === 'string' ? firstError : '';
  if (typeof firstError === 'object' && 'longMessage' in firstError) {
    errStr = firstError.longMessage ?? '';
  }

  if (errStr.length === 0 && typeof firstError === 'object' && 'message' in firstError) {
    errStr = firstError.message ?? '';
  }

  if (errStr == null || errStr.length === 0) {
    return undefined;
  }

  // NOTE: we want to do it this way because we have special errors that come
  //       as long strings, like clerk, so we want to check the errors table
  //       first before capturing the error of the missing
  let hasTranslation = errorsTranslations.errors[errStr] != null;
  if (hasTranslation) {
    return t(errStr, undefined, true);
  }

  // maybe it is in uppercase
  hasTranslation = errorsTranslations.errors[errStr.toUpperCase()] != null;
  if (hasTranslation) {
    return t(errStr.toUpperCase(), undefined, true);
  }

  return t(errStr);
}

export function normalizeErrors(errors?: ErrorAny | ErrorAny[]) {
  const rawErrors = Array.isArray(errors) ? errors : [errors];
  const mappedErrors = [] as string[];
  for (let i = 0; i < rawErrors.length; i += 1) {
    const err = rawErrors[i];
    if (err == null) {
      continue;
    }

    // this will bring a translated key
    const newErr = mapErrorToTranslation(err);
    if (newErr != null && newErr.length > 0) {
      mappedErrors.push(newErr);
    }
  }

  return mappedErrors;
}
